import {
  getAuthoredDescriptor,
  type AuthoredBank,
  type AuthoredPitch,
} from '../builder-v2/index.ts';

export type BasicIntentId =
  | 'direction.straight'
  | 'direction.gentle_left'
  | 'direction.sharp_left'
  | 'direction.gentle_right'
  | 'direction.sharp_right'
  | 'grade.down'
  | 'grade.level'
  | 'grade.up'
  | 'bank.left'
  | 'bank.level'
  | 'bank.right';

export type BasicIntentAxis = 'direction' | 'grade' | 'bank';
export type SpecialTrackGroup = 'loops' | 'rolls' | 'corkscrews' | 'helixes' | 'other';

export interface BasicCatalogEntry {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly selected: boolean;
  readonly allowed: boolean;
  readonly reason: string | null;
  readonly recommendedRank: number | null;
}

export interface ContextualSpecialTrackEntry<T extends BasicCatalogEntry = BasicCatalogEntry> {
  readonly entry: T;
  readonly displayLabel: string;
  readonly selectionKey: string;
}

export type BasicDirectionId =
  | 'direction.left.small'
  | 'direction.left.large'
  | 'direction.left.45'
  | 'direction.straight'
  | 'direction.right.45'
  | 'direction.right.large'
  | 'direction.right.small';

export type BasicSlopeId =
  'slope.n90' | 'slope.n60' | 'slope.n25' | 'slope.0' | 'slope.25' | 'slope.60' | 'slope.90';

export type BasicBankId = 'bank.left' | 'bank.level' | 'bank.right';
export type BasicRegularAxis = 'direction' | 'slope' | 'bank';
export type BasicRegularChoice = BasicDirectionId | BasicSlopeId | BasicBankId;

export interface BasicRegularSelection {
  readonly direction: BasicDirectionId;
  readonly slope: BasicSlopeId;
  readonly bank: BasicBankId;
}

export const BASIC_DIRECTION_CHOICES: readonly {
  readonly id: BasicDirectionId;
  readonly label: string;
  readonly accessibleLabel: string;
}[] = [
  { id: 'direction.left.small', label: 'Small', accessibleLabel: 'Small 90 degree left turn' },
  { id: 'direction.left.large', label: 'Large', accessibleLabel: 'Large 90 degree left turn' },
  { id: 'direction.left.45', label: '45°', accessibleLabel: '45 degree left turn' },
  { id: 'direction.straight', label: 'Straight', accessibleLabel: 'Straight' },
  { id: 'direction.right.45', label: '45°', accessibleLabel: '45 degree right turn' },
  { id: 'direction.right.large', label: 'Large', accessibleLabel: 'Large 90 degree right turn' },
  { id: 'direction.right.small', label: 'Small', accessibleLabel: 'Small 90 degree right turn' },
] as const;

export const BASIC_SLOPE_CHOICES: readonly {
  readonly id: BasicSlopeId;
  readonly label: string;
  readonly pitch: AuthoredPitch;
}[] = [
  { id: 'slope.n90', label: '90°', pitch: -90 },
  { id: 'slope.n60', label: '60°', pitch: -60 },
  { id: 'slope.n25', label: '25°', pitch: -25 },
  { id: 'slope.0', label: 'Level', pitch: 0 },
  { id: 'slope.25', label: '25°', pitch: 25 },
  { id: 'slope.60', label: '60°', pitch: 60 },
  { id: 'slope.90', label: '90°', pitch: 90 },
] as const;

export const BASIC_BANK_CHOICES: readonly {
  readonly id: BasicBankId;
  readonly label: string;
  readonly bank: AuthoredBank;
}[] = [
  { id: 'bank.left', label: 'Left', bank: 'left' },
  { id: 'bank.level', label: 'Level', bank: 'level' },
  { id: 'bank.right', label: 'Right', bank: 'right' },
] as const;

export const BASIC_INTENT_ROWS: readonly {
  readonly axis: BasicIntentAxis;
  readonly label: string;
  readonly choices: readonly { readonly id: BasicIntentId; readonly label: string }[];
}[] = [
  {
    axis: 'direction',
    label: 'Direction',
    choices: [
      { id: 'direction.gentle_left', label: 'Gentle Left' },
      { id: 'direction.sharp_left', label: 'Sharp Left' },
      { id: 'direction.straight', label: 'Straight' },
      { id: 'direction.gentle_right', label: 'Gentle Right' },
      { id: 'direction.sharp_right', label: 'Sharp Right' },
    ],
  },
  {
    axis: 'grade',
    label: 'Grade',
    choices: [
      { id: 'grade.down', label: 'Down' },
      { id: 'grade.level', label: 'Level' },
      { id: 'grade.up', label: 'Up' },
    ],
  },
  {
    axis: 'bank',
    label: 'Bank',
    choices: [
      { id: 'bank.left', label: 'Left' },
      { id: 'bank.level', label: 'Level' },
      { id: 'bank.right', label: 'Right' },
    ],
  },
] as const;

export const SPECIAL_TRACK_GROUPS: readonly {
  readonly id: SpecialTrackGroup;
  readonly label: string;
}[] = [
  { id: 'loops', label: 'Loops' },
  { id: 'rolls', label: 'Rolls' },
  { id: 'corkscrews', label: 'Corkscrews' },
  { id: 'helixes', label: 'Helixes' },
  { id: 'other', label: 'Other Special Track' },
] as const;

function descriptorPitch(id: string): AuthoredPitch | null {
  const descriptor = getAuthoredDescriptor(id);
  if (!descriptor) return null;
  const exit = descriptor.exit.pitch;
  if (exit !== 'preserve') return exit;
  return descriptor.entry.pitches.length === 1 ? descriptor.entry.pitches[0]! : null;
}

function descriptorBank(id: string): AuthoredBank | null {
  const descriptor = getAuthoredDescriptor(id);
  if (!descriptor) return null;
  const exit = descriptor.exit.bank;
  if (exit !== 'preserve') return exit;
  return descriptor.entry.banks.length === 1 ? descriptor.entry.banks[0]! : null;
}

export function specialTrackGroup(id: string): SpecialTrackGroup | null {
  const descriptor = getAuthoredDescriptor(id);
  if (!descriptor) return null;
  if (descriptor.family === 'vertical_loop' || descriptor.family === 'half_loop') return 'loops';
  if (
    descriptor.family === 'barrel_roll' ||
    descriptor.family === 'half_roll' ||
    descriptor.family === 'zero_g_roll'
  )
    return 'rolls';
  if (descriptor.family === 'corkscrew' || descriptor.family === 'half_corkscrew')
    return 'corkscrews';
  if (descriptor.family === 'helix') return 'helixes';
  if (descriptor.id.startsWith('s_bend.') || descriptor.tags.includes('s-bend')) return 'other';
  return descriptor.category === 'inversion' ? 'other' : null;
}

export function isSpecialTrackDescriptor(id: string): boolean {
  return specialTrackGroup(id) !== null;
}

export function primaryIntentForDescriptor(id: string): BasicIntentId | null {
  const descriptor = getAuthoredDescriptor(id);
  if (!descriptor || descriptor.category === 'station' || isSpecialTrackDescriptor(id)) return null;
  if (descriptor.category === 'straight') return 'direction.straight';
  if (descriptor.category === 'turn') {
    const left = descriptor.exit.headingDelta < 0;
    const sharp = Math.abs(descriptor.exit.headingDelta) >= 2;
    return `direction.${sharp ? 'sharp' : 'gentle'}_${left ? 'left' : 'right'}`;
  }
  if (descriptor.category === 'slope') {
    const pitch = descriptorPitch(id);
    return pitch === 0 ? 'grade.level' : pitch !== null && pitch < 0 ? 'grade.down' : 'grade.up';
  }
  if (descriptor.category === 'bank') {
    const bank = descriptorBank(id) ?? 'level';
    return `bank.${bank}`;
  }
  return null;
}

function isSlopeHoldForPitch(id: string, pitch: AuthoredPitch): boolean {
  const descriptor = getAuthoredDescriptor(id);
  return (
    descriptor?.category === 'slope' &&
    descriptor.exit.pitch === 'preserve' &&
    descriptor.entry.pitches.length === 1 &&
    descriptor.entry.pitches[0] === pitch
  );
}

export function basicIntentVariants<T extends BasicCatalogEntry>(
  entries: readonly T[],
  intent: BasicIntentId,
  currentPitch: AuthoredPitch,
): readonly T[] {
  return entries.filter((entry) => {
    if (isSpecialTrackDescriptor(entry.id)) return false;
    if (intent === 'direction.straight') {
      const descriptor = getAuthoredDescriptor(entry.id);
      return descriptor?.category === 'straight' || isSlopeHoldForPitch(entry.id, currentPitch);
    }
    if (primaryIntentForDescriptor(entry.id) === intent) return true;
    if (intent === 'grade.level' || intent === 'bank.level') {
      return getAuthoredDescriptor(entry.id)?.category === 'straight';
    }
    return false;
  });
}

function preferredScore(entry: BasicCatalogEntry, selectedId: string): number {
  if (!entry.allowed) return 1000;
  if (entry.id === selectedId) return -1000;
  if (entry.recommendedRank !== null) return entry.recommendedRank - 100;
  if (/Â½ tile|1 tile|Small/.test(entry.label)) return 0;
  if (/2 tiles|Medium/.test(entry.label)) return 10;
  return 20;
}

export function preferredBasicIntentEntry<T extends BasicCatalogEntry>(
  entries: readonly T[],
  intent: BasicIntentId,
  currentPitch: AuthoredPitch,
  selectedId: string,
): T | null {
  return (
    [...basicIntentVariants(entries, intent, currentPitch)].sort(
      (first, second) => preferredScore(first, selectedId) - preferredScore(second, selectedId),
    )[0] ?? null
  );
}

export function basicAxesForSelection(
  selectedId: string,
  currentPitch: AuthoredPitch,
  currentBank: AuthoredBank,
): Readonly<Record<BasicIntentAxis, BasicIntentId>> {
  const primary = primaryIntentForDescriptor(selectedId);
  const direction = primary?.startsWith('direction.') ? primary : 'direction.straight';
  const grade = primary?.startsWith('grade.')
    ? primary
    : currentPitch < 0
      ? 'grade.down'
      : currentPitch > 0
        ? 'grade.up'
        : 'grade.level';
  const bank = primary?.startsWith('bank.') ? primary : `bank.${currentBank}`;
  return { direction, grade, bank } as Readonly<Record<BasicIntentAxis, BasicIntentId>>;
}

const BASIC_DIRECTION_DESCRIPTOR_IDS: Readonly<
  Record<Exclude<BasicDirectionId, 'direction.straight'>, string>
> = {
  'direction.left.small': 'turn.left.90.small',
  'direction.left.large': 'turn.left.90.medium',
  'direction.left.45': 'turn.left.45.medium',
  'direction.right.45': 'turn.right.45.medium',
  'direction.right.large': 'turn.right.90.medium',
  'direction.right.small': 'turn.right.90.small',
};

const BASIC_DESCRIPTOR_DIRECTIONS = new Map<string, BasicDirectionId>(
  Object.entries(BASIC_DIRECTION_DESCRIPTOR_IDS).map(([direction, id]) => [
    id,
    direction as BasicDirectionId,
  ]),
);

function basicSlopeId(pitch: AuthoredPitch): BasicSlopeId {
  return `slope.${pitch < 0 ? `n${Math.abs(pitch)}` : pitch}` as BasicSlopeId;
}

function basicBankId(bank: AuthoredBank): BasicBankId {
  return `bank.${bank}` as BasicBankId;
}

export function basicSelectionForDescriptor(
  id: string,
  currentPitch: AuthoredPitch,
  currentBank: AuthoredBank,
): BasicRegularSelection | null {
  const descriptor = getAuthoredDescriptor(id);
  if (!descriptor || descriptor.category === 'station' || isSpecialTrackDescriptor(id)) return null;
  const direction =
    descriptor.category === 'turn'
      ? (BASIC_DESCRIPTOR_DIRECTIONS.get(id) ?? null)
      : ('direction.straight' as const);
  const pitch = descriptor.exit.pitch === 'preserve' ? currentPitch : descriptor.exit.pitch;
  const bank = descriptor.exit.bank === 'preserve' ? currentBank : descriptor.exit.bank;
  if (!direction) return null;
  return { direction, slope: basicSlopeId(pitch), bank: basicBankId(bank) };
}

export function defaultBasicSelection(
  currentPitch: AuthoredPitch,
  currentBank: AuthoredBank,
): BasicRegularSelection {
  return {
    direction: 'direction.straight',
    slope: basicSlopeId(currentPitch),
    bank: basicBankId(currentBank),
  };
}

function canonicalBasicPieceScore(entry: BasicCatalogEntry): number {
  if (!entry.allowed) return Number.POSITIVE_INFINITY;
  if (entry.recommendedRank !== null) return -100 + entry.recommendedRank;
  if (entry.id.endsWith('.1t') || entry.id.endsWith('run1t')) return 0;
  if (entry.id.endsWith('.0_5t') || entry.id.endsWith('run0_5t')) return 10;
  return 30;
}

function contextualSpecialKey(id: string): string {
  const descriptor = getAuthoredDescriptor(id);
  if (descriptor?.family === 'half_loop') return 'half_loop';
  if (descriptor?.family === 'half_roll') return `half_roll.${id.split('.')[1] ?? 'unknown'}`;
  if (descriptor?.family === 'half_corkscrew')
    return `half_corkscrew.${id.split('.')[1] ?? 'unknown'}`;
  return id;
}

function contextualSpecialLabel(id: string, fallback: string): string {
  const descriptor = getAuthoredDescriptor(id);
  if (descriptor?.family === 'half_loop') return 'Half Loop';
  if (descriptor?.family === 'half_roll')
    return `${id.includes('.left.') ? 'Left' : 'Right'} Half Roll`;
  if (descriptor?.family === 'half_corkscrew')
    return `${id.includes('.left.') ? 'Left' : 'Right'} Half Corkscrew`;
  return fallback;
}

export function contextualSpecialTrackEntries<T extends BasicCatalogEntry>(
  entries: readonly T[],
): readonly ContextualSpecialTrackEntry<T>[] {
  const grouped = new Map<string, T[]>();
  for (const entry of entries) {
    const key = contextualSpecialKey(entry.id);
    grouped.set(key, [...(grouped.get(key) ?? []), entry]);
  }
  return [...grouped.values()].map((variants) => {
    const entry =
      variants.find((candidate) => candidate.allowed) ??
      variants.find((candidate) => candidate.selected) ??
      variants[0]!;
    return {
      entry,
      displayLabel: contextualSpecialLabel(entry.id, entry.label),
      selectionKey: contextualSpecialKey(entry.id),
    };
  });
}

export function contextualSpecialSuccessor<T extends BasicCatalogEntry>(
  entries: readonly T[],
  selectedId: string,
): T | null {
  const selectedKey = contextualSpecialKey(selectedId);
  return (
    contextualSpecialTrackEntries(entries).find(
      ({ entry, selectionKey }) => selectionKey === selectedKey && entry.allowed,
    )?.entry ?? null
  );
}

export function resolveBasicRegularPiece<T extends BasicCatalogEntry>(
  entries: readonly T[],
  selection: BasicRegularSelection,
  currentPitch: AuthoredPitch,
  currentBank: AuthoredBank,
): T | null {
  const matches = entries.filter((entry) => {
    if (!entry.allowed) return false;
    const candidate = basicSelectionForDescriptor(entry.id, currentPitch, currentBank);
    return (
      candidate?.direction === selection.direction &&
      candidate.slope === selection.slope &&
      candidate.bank === selection.bank
    );
  });
  // A level, unbanked Basic straight has one public meaning: one tile. Route
  // Guide recommendations and previously selected legacy lengths must not turn
  // this control into an unpredictable half-, two-, or three-tile placement.
  // The runtime owns the sole exception: an exact half-tile station seam.
  if (
    selection.direction === 'direction.straight' &&
    selection.slope === 'slope.0' &&
    selection.bank === 'bank.level'
  ) {
    const oneTileStraight = matches.find((entry) => entry.id === 'straight.1t');
    if (oneTileStraight) return oneTileStraight;
  }
  return (
    matches.sort(
      (first, second) => canonicalBasicPieceScore(first) - canonicalBasicPieceScore(second),
    )[0] ?? null
  );
}

export function selectionWithChoice(
  selection: BasicRegularSelection,
  axis: BasicRegularAxis,
  choice: BasicRegularChoice,
): BasicRegularSelection {
  return { ...selection, [axis]: choice } as BasicRegularSelection;
}

export function basicChoiceAvailable<T extends BasicCatalogEntry>(
  entries: readonly T[],
  selection: BasicRegularSelection,
  axis: BasicRegularAxis,
  choice: BasicRegularChoice,
  currentPitch: AuthoredPitch,
  currentBank: AuthoredBank,
): boolean {
  return Boolean(
    resolveBasicRegularPiece(
      entries,
      selectionWithChoice(selection, axis, choice),
      currentPitch,
      currentBank,
    ),
  );
}

export function basicDisplayLabel(id: string, fallback: string): string {
  const labels: Readonly<Record<string, string>> = {
    'turn.left.90.small': 'Small Left Turn',
    'turn.left.90.medium': 'Large Left Turn',
    'turn.left.45.medium': '45° Left Turn',
    'turn.right.45.medium': '45° Right Turn',
    'turn.right.90.medium': 'Large Right Turn',
    'turn.right.90.small': 'Small Right Turn',
    'straight.0_5t': 'Straight · ½ tile',
    'straight.1t': 'Straight',
  };
  return labels[id] ?? fallback;
}
